import Phaser from 'phaser';
import * as ABCya from '@abcya/abcya_phaser';
import GameData, { getFoundShapesForAreaShapes, saveGameData, SHAPES } from "./gameData";
import Region from "./Region";
import UIAssetKeys from './UIAssetKeys';
import FontKeys from './FontKeys';
import SoundAssetKeys from './SoundAssetKeys';

/**
 * A RegionArea represents a single area within a region. This class should be extended.
 * A RegionArea isn't a displayable object. It instead uses the objects found in the parent region,
 * and adds objects to the parent region as needed.
 */
export default class RegionArea
{
    /**
     * Create the RegionArea
     * @param {Region} parentRegion the owner region
     * @param {string} area the AREAS enum value
     */
    constructor(parentRegion, area)
    {
        this.parentRegion = parentRegion;
        this.region = this.parentRegion.region;
        this.area = area;

        this.shapeOverlays = {};
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        this._sendObjectsToStartPositions();

        this.setAreaShapeButtonInteractable(false);
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        this._sendObjectsToStartPositions();

        this.setAreaShapeButtonInteractable(false);

        this.updateAllShapeAreaInformations();

        this._resetAreaInformationState();
    }

    /**
     * Sets the shape buttons to be interactable or not.
     * @param {Boolean} isInteractable whether the shape buttons should be interactable.
     */
    setAreaShapeButtonInteractable(isInteractable)
    {
        for (let shape in GameData.region_data[this.region][this.area])
        {
            if (isInteractable)
            {
                this.areaInformation[shape].shapeButton.setInteractive();
            }
            else this.areaInformation[shape].shapeButton.disableInteractive();
        }
    }

    /**
     * Update a shape information display.
     * @param {string} shape the SHAPES enum value
     */
    _updateShapeAreaInformation(shape)
    {
        let count = getFoundShapesForAreaShapes(this.region, this.area, shape);
        this.areaInformation[shape].shapeCountText.text = count + "/" + GameData.region_data[this.region][this.area][shape];
        this.areaInformation[shape].checkmark.setAlpha(count == GameData.region_data[this.region][this.area][shape] ? 1 : 0);
    }

    /**
     * Updates all the shape information panels.
     */
    updateAllShapeAreaInformations()
    {
        for (let shape in GameData.region_data[this.region][this.area])
        {
            this._updateShapeAreaInformation(shape);
        }
    }

    /**
     * Send all game objects to their start positions.
     */
    _sendObjectsToStartPositions()
    {
        this.areaInformation.setPosition(this.areaInformation.offScreenPosition.x, this.areaInformation.offScreenPosition.y);
    }

    /**
     * Resets the area information shape backgrounds to be not selected.
     */
    _resetAreaInformationState()
    {
        for (let shape in GameData.region_data[this.region][this.area])
        {
            this.areaInformation[shape].shapeBg.setFrame(this.areaInformation[shape].shapeNormalBg);
            this.areaInformation[shape].isSelected = false;
        }
    }

    /**
     * Creates the shape information for the area. These are the buttons that can be clicked to find the shapes.
     * @param {number} numberFontColor the number font color
     * @param {number} textFontColor the text font color
     */
    _createAreaInformation(numberFontColor, textFontColor)
    {
        this.areaInformation = this.parentRegion.scene.add.container();
        this.parentRegion.add(this.areaInformation);

        let yPos = 297;
        let maxWidth = 0;
        let minX = 1000;

        // for each shape in the area, create a shape information
        for (let shape in GameData.region_data[this.region][this.area])
        {
            this.areaInformation[shape] = {};

            /**
             * @typedef {object} ShapeContainer
             * @property {Phaser.GameObjects.Image} shapeBg the shape icon image object
             * @property {string} shapeSelectedBg the shape icon when the shape is selected
             * @property {string} shapeNormalBg the normal shape icon key
             * @property {Phaser.GameObjects.Text} shapeCountText the shape count text object
             * @property {Phaser.GameObjects.Text} shapeText the shape name text object
             * @property {Phaser.GameObjects.Image} checkmark the checkmark image that is displayed when enough shapes have been found
             * @property {Phaser.GameObjects.Zone} shapeButton the invisible button that the user can click to select the shape
             * @property {Boolean} isSelected whether this shape container is selected
             */

            /**
             * @type {ShapeContainer}
             */
            let containerShapeObj = this.areaInformation[shape];
            /**
             * @type {Phaser.GameObjects.Image}
             */
            containerShapeObj.shapeBg = null;
            let countTextLoc = { x: 78, y: yPos };
            switch (shape)
            {
                case SHAPES.CIRCLE:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos, UIAssetKeys._04_Zoom_Shapes_Shp_Crcl, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Crcl_Ylw;
                    break;
                case SHAPES.CONE:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos - 20, UIAssetKeys._04_Zoom_Shapes_Shp_Cone, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Cone_Ylw;
                    break;
                case SHAPES.CYLINDER:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos - 15, UIAssetKeys._04_Zoom_Shapes_Shp_Clndr, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Clndr_Ylw;
                    break;
                case SHAPES.HEXAGON:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos, UIAssetKeys._04_Zoom_Shapes_Shp_Hex, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Hex_Ylw;
                    break;
                case SHAPES.PENTAGON:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos - 4, UIAssetKeys._04_Zoom_Shapes_Shp_Penta, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Penta_Ylw;
                    break;
                case SHAPES.PYRAMID:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos - 5, UIAssetKeys._04_Zoom_Shapes_Shp_Prmd, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Prmd_Ylw;
                    break;
                case SHAPES.RECTANGLE:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos, UIAssetKeys._04_Zoom_Shapes_Shp_Rct, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Rct_Ylw;
                    break;
                case SHAPES.RECTANGULAR_PRISM:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos, UIAssetKeys._04_Zoom_Shapes_Shp_RctPrsm, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_RctPrsm_Ylw;
                    break;
                case SHAPES.SPHERE:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos, UIAssetKeys._04_Zoom_Shapes_Shp_Sphr, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Sphr_Ylw;
                    break;
                case SHAPES.SQUARE:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos, UIAssetKeys._04_Zoom_Shapes_Shp_Sqr, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Sqr_Ylw;
                    break;
                case SHAPES.TRIANGLE:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(78, yPos - 18, UIAssetKeys._04_Zoom_Shapes_Shp_Trngl, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_Trngl_1;
                    break;
                case SHAPES.TRIANGULAR_PRISM:
                    containerShapeObj.shapeBg = ABCya.Images.addImage(72, yPos - 4, UIAssetKeys._04_Zoom_Shapes_Shp_TringlrPrsm, this.areaInformation);
                    containerShapeObj.shapeSelectedBg = UIAssetKeys._04_Zoom_Shapes_Shp_TringlrPrsm_Ylw;
                    break;
            }
            containerShapeObj.shapeNormalBg = containerShapeObj.shapeBg.frame.name;
            containerShapeObj.shapeCountText = ABCya.Text.addText(countTextLoc.x, countTextLoc.y, 24, numberFontColor, FontKeys._graystrokeregular, "0/" + GameData.region_data[this.region][this.area][shape], this.areaInformation);
            containerShapeObj.shapeText = ABCya.Text.addText(139, yPos + 4, 44, textFontColor, FontKeys._retrigaregular, GameData.region_data[this.region][this.area][shape] + " " + shape + (GameData.region_data[this.region][this.area][shape] > 1 ? "s" : ""), this.areaInformation, false).setOrigin(0, 0.5);
            let width = containerShapeObj.shapeBg.width + containerShapeObj.shapeText.width + (containerShapeObj.shapeText.x - containerShapeObj.shapeBg.x - (containerShapeObj.shapeBg.width / 2));
            containerShapeObj.checkmark = ABCya.Images.addImage((78 - containerShapeObj.shapeBg.width / 2) + width + 10, yPos, UIAssetKeys._05_Beach_Zoom_In_Chkmrk, this.areaInformation).setOrigin(0, 0.5);
            containerShapeObj.shapeButton = this.parentRegion.scene.add.zone(containerShapeObj.shapeBg.x - containerShapeObj.shapeBg.width / 2,
                yPos - containerShapeObj.shapeBg.height / 2,
                width,
                containerShapeObj.shapeBg.height).setInteractive({ useHandCursor: true }).setOrigin(0);
            this.areaInformation.add(containerShapeObj.shapeButton);

            width += containerShapeObj.checkmark.width + 15;
            maxWidth = width > maxWidth ? width : maxWidth;
            minX = minX > containerShapeObj.shapeBg.x - containerShapeObj.shapeBg.width / 2 ? containerShapeObj.shapeBg.x - containerShapeObj.shapeBg.width / 2 : minX;

            yPos += 112;

            containerShapeObj.shapeButton.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => 
            {
                if (!containerShapeObj.isSelected)
                {
                    containerShapeObj.isSelected = true;
                    ABCya.Audio.playOneShot(SoundAssetKeys._select_shape, this.parentRegion.scene);
                    for (let shapeInOverlay in this.shapeOverlays)
                    {
                        if (shapeInOverlay != shape)
                        {
                            this.areaInformation[shapeInOverlay].shapeBg.setFrame(this.areaInformation[shapeInOverlay].shapeNormalBg);
                            this.areaInformation[shapeInOverlay].isSelected = false;
                            this.parentRegion.scene.tweens.killTweensOf(this.shapeOverlays[shapeInOverlay]);
                            ABCya.Tweens.addTween(this.shapeOverlays[shapeInOverlay], 350, { alpha: 0.00001 }, Phaser.Math.Easing.Quadratic.In);
                            this.shapeOverlays[shapeInOverlay].forEach((overlay) => 
                            {
                                overlay.disableInteractive();
                            });
                        }
                    }
                    this.areaInformation[shape].shapeBg.setFrame(this.areaInformation[shape].shapeSelectedBg);
                    this.shapeOverlays[shape].forEach((overlay) => 
                    {
                        if (GameData.saved_data[this.region][this.area].shapes[shape][this.shapeOverlays[shape].indexOf(overlay)])
                        {
                            this.parentRegion.scene.tweens.killTweensOf(overlay);
                            ABCya.Tweens.addTween(overlay, 350, { alpha: 1, delay: 300 }, Phaser.Math.Easing.Quadratic.Out);
                        }
                        else
                        {
                            overlay.setInteractive();
                        }
                    });
                }
            });
        }

        this.parentRegion._setAnimationPositions(this.areaInformation, { x: -(maxWidth + minX), y: this.areaInformation.y });
    }

    /**
     * Callback function when an overlay is clicked on.
     * @param {Phaser.GameObjects.Image} overlay the shape overlay that was clicked on
     * @param {string} shape the SHAPE enum value that the overlay belongs to
     */
    _onOverlayPointerDown(overlay, shape)
    {
        ABCya.Audio.playOneShot(SoundAssetKeys._find_shape, this.parentRegion.scene);
        ABCya.Tweens.addTween(overlay, 500, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out);
        overlay.disableInteractive();
        GameData.saved_data[this.region][this.area].shapes[shape][this.shapeOverlays[shape].indexOf(overlay)] = true;
        saveGameData();

        this._updateShapeAreaInformation(shape);

        let allNeededShapesFound = true;
        for (let shape in GameData.region_data[this.region][this.area])
        {
            if (getFoundShapesForAreaShapes(this.region, this.area, shape) < GameData.region_data[this.region][this.area][shape])
            {
                allNeededShapesFound = false;
                break;
            }
        }
        if (!this.allShapesFound && allNeededShapesFound)
        {
            // show the great job popup
            ABCya.Audio.playOneShot(SoundAssetKeys._great_work, this.parentRegion.scene, 1, false, 0.4);
            this.parentRegion.greatWorkText.setScale(0);
            let tweens = [
                { targets: this.parentRegion.greatWorkPopup, duration: 400, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0 },
                { targets: this.parentRegion.greatWorkText, duration: 400, props: { scale: 1 }, ease: Phaser.Math.Easing.Back.Out, offset: 200 },
                { targets: [this.parentRegion.greatWorkPopup], duration: 500, props: { alpha: 0 }, ease: Phaser.Math.Easing.Quadratic.In, offset: 2500 },
            ];

            this.parentRegion.scene.tweens.timeline({
                tweens: tweens,
                onComplete: () =>
                {
                    // go back to the region
                    this.parentRegion._leaveZoomedInState(true);
                }
            });
        }
    }

    /**
     * Zoom into this area. This prepares the area.
     */
    zoomIn()
    {
        GameData.saved_data[this.region][this.area].found = true;
        saveGameData();

        this.prepare();

        this.allShapesFound = true;
        for (let shape in GameData.region_data[this.region][this.area])
        {
            if (getFoundShapesForAreaShapes(this.region, this.area, shape) < GameData.region_data[this.region][this.area][shape])
            {
                this.allShapesFound = false;
                break;
            }
        }

        return [];
    }

    /**
     * Zoom out of this area. This sets the shape buttons to be not interactable.
     */
    zoomOut()
    {
        this.setAreaShapeButtonInteractable(false);
        return [];
    }
}